import { Dropdown, MenuProps } from "antd";
import clsx from "clsx";
import CheckCircleIcon from "mdi-react/CheckCircleIcon";
import CheckboxMarkedIcon from "mdi-react/CheckboxMarkedIcon";
import CheckboxBlankOutlineIcon from "mdi-react/CheckboxBlankOutlineIcon";
import CircleOutlineIcon from "mdi-react/CircleOutlineIcon";
import ChevronDownIcon from "mdi-react/ChevronDownIcon";
import { ComponentType, useEffect, useState } from "react";

type SelectOptionValueType = string | number | boolean;

interface SelectOptionProps<T = {}> {
  value: SelectOptionValueType;
  title: string;
  data?: T;
}

interface AppSelectProps<T> {
  valid?: boolean;
  invalid?: boolean;
  invalidText?: string;
  containerClassName?: string;
  selectClassName?: string;
  items: SelectOptionProps<T>[];
  placeholder?: string;
  value?: SelectOptionValueType | SelectOptionValueType[];
  onChangeValue?: (param: SelectOptionProps<T> | SelectOptionProps<T>[]) => void;
  disabled?: boolean;
  mode?: "single" | "multiple";
  showSearchInput?: boolean;
  TitleComponent?: ComponentType<{ title: string; data?: T }>;
  OptionComponent?: ComponentType<{ data?: T }>;
}

const AppSelect = <T,>(props: AppSelectProps<T>) => {
  const {
    // valid = false,
    invalid = false,
    invalidText = "",
    containerClassName = "",
    selectClassName = "",
    items = [],
    onChangeValue,
    mode = "single",
    value,
    placeholder = "",
    disabled = false,
    TitleComponent,
    OptionComponent,
  } = props;

  const [selectedOption, setSelectedOption] = useState<SelectOptionProps<T>>();
  const [selectedOptions, setSelectedOptions] = useState<SelectOptionProps<T>[]>([]);

  const handleChange = (option: SelectOptionProps<T>) => {
    if (mode === "single") {
      onSingleSelect(option);
    } else {
      onMultipleSelect(option);
    }
  };

  const onMultipleSelect = (option: SelectOptionProps<T>) => {
    let newList = [...selectedOptions];
    if (newList?.some((opt) => opt.value === option.value)) {
      newList = newList.filter((oldOption) => oldOption.value !== option.value);
    } else {
      newList.push(option);
    }
    setSelectedOptions(newList);
    onChangeValue && onChangeValue(newList);
  };

  const onSingleSelect = (value: SelectOptionProps<T>) => {
    setSelectedOption(value);
    onChangeValue && onChangeValue(value);
  };

  const validateChecked = (item: SelectOptionProps<T>) => {
    return (
      (mode === "multiple" && selectedOptions.some((opt) => opt.value === item.value)) ||
      (mode === "single" && selectedOption?.value === item.value)
    );
  };

  const dropdownItems: MenuProps["items"] = items.map((opt, idx) => ({
    label: (
      <div
        onClick={(e) => {
          e.stopPropagation();
          handleChange(opt);
        }}
        className="flex items-center justify-start"
      >
        <div>
          {validateChecked(opt) ? (
            <>
              {mode === "single" ? (
                <CheckCircleIcon className="text-primary" size={16} />
              ) : (
                <CheckboxMarkedIcon className="text-primary" size={16} />
              )}
            </>
          ) : (
            <>
              {mode === "single" ? (
                <CircleOutlineIcon className="text-primary" size={16} />
              ) : (
                <CheckboxBlankOutlineIcon className="text-primary" size={16} />
              )}
            </>
          )}
        </div>
        {OptionComponent ? (
          <OptionComponent data={opt.data} />
        ) : (
          <p className="font-normal text-sm ml-2">{opt.title}</p>
        )}
      </div>
    ),
    key: idx.toString(),
  }));

  useEffect(() => {
    if (Array.isArray(value)) {
      let findOptions = items?.filter((opt) => value.includes(opt.value));
      setSelectedOptions(findOptions);
    } else {
      let findOption = items?.find((opt) => opt.value === value);
      setSelectedOption(findOption);
    }
  }, [value, items]);

  return (
    <div
      className={clsx("flex flex-col cursor-pointer rounded-md", containerClassName, {
        "bg-light-500": disabled,
      })}
    >
      <Dropdown
        menu={{ items: dropdownItems }}
        trigger={["click"]}
        arrow
        placement="bottom"
        className={selectClassName}
        disabled={disabled}
      >
        <div
          className={clsx(
            "disabled:opacity-50 disabled:pointer-events-none border-[1px] border-solid border-gray hover:border-primary rounded-md min-h-[40px] w-full flex items-center justify-between px-4",
            {
              "hover:border-dark-100": !disabled,
              "opacity-40 pointer-events-none": disabled,
              "border-danger": invalid,
            },
          )}
        >
          <p className="text-md lg:text-sm text-dark">
            {mode === "single" ? (
              TitleComponent ? (
                <TitleComponent title={selectedOption?.title || placeholder} data={selectedOption?.data} />
              ) : (
                selectedOption?.title || placeholder
              )
            ) : selectedOptions?.length ? (
              `Đã chọn ${selectedOptions?.length} lựa chọn`
            ) : (
              placeholder
            )}
          </p>
          <ChevronDownIcon size={20} className="text-gray-100" />
        </div>
      </Dropdown>
      {invalid && <p className="mt-2 text-xs text-danger font-medium">{invalidText}</p>}
    </div>
  );
};

export default AppSelect;
